import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import './FeaturedAdsCarousel.css';

function FeaturedAdsCarousel({ categoryId = null, subcategoryId = null }) {
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchAds = useCallback(async () => {
    try {
      setLoading(true);
      const today = new Date();

      let q = query(
        collection(db, 'featuredAds'),
        where('status', 'in', ['live', 'draft'])
      );

      const querySnapshot = await getDocs(q);
      let adsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter ads by category, subcategory, budget, and dates
      adsData = adsData.filter(ad => {
        // Check if ad has run out of budget
        const spend = ad.spend || 0;
        const budget = ad.budget || 0;
        if (spend >= budget) return false;

        // Check if ad is expired
        if (ad.runTill) {
          const runTill = ad.runTill.toDate ? ad.runTill.toDate() : new Date(ad.runTill);
          if (runTill < today) return false;
        }

        // Filter by category if provided
        if (categoryId) {
          if (ad.category !== categoryId && ad.categoryId !== categoryId) {
            return false;
          }
        }

        // Filter by subcategory if provided
        if (subcategoryId) {
          const adSubcategories = ad.subcategoryIds || ad.subCategoryIds || [];
          const adSubcategory = ad.subcategory || ad.subcategoryId || ad.subCategoryId;
          if (adSubcategory !== subcategoryId && !adSubcategories.includes(subcategoryId)) {
            return false;
          }
        }

        return true;
      });

      setAds(adsData);
      if (adsData.length > 0) {
        // Track impression for first ad
        trackImpression(adsData[0].id);
      }
    } catch (error) {
      console.error('Error fetching featured ads:', error);
    } finally {
      setLoading(false);
    }
  }, [categoryId, subcategoryId]);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  const trackImpression = async (adId) => {
    try {
      const adRef = doc(db, 'featuredAds', adId);
      await updateDoc(adRef, {
        impressions: increment(1)
      });
    } catch (error) {
      console.error('Error tracking impression:', error);
    }
  };

  const trackClick = async (adId) => {
    try {
      const adRef = doc(db, 'featuredAds', adId);
      await updateDoc(adRef, {
        clicks: increment(1),
        spend: increment(0.10) // CPC: $0.10 per click
      });
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const handleAdClick = (ad) => {
    trackClick(ad.id);
    if (ad.url) {
      window.open(ad.url, '_blank', 'noopener,noreferrer');
    } else if (ad.businessId) {
      window.location.href = `/business/${ad.businessId}`;
    }
  };

  const nextAd = useCallback(() => {
    if (ads.length > 0) {
      const newIndex = (currentIndex + 1) % ads.length;
      setCurrentIndex(newIndex);
      trackImpression(ads[newIndex].id);
    }
  }, [currentIndex, ads]);

  const prevAd = () => {
    if (ads.length > 0) {
      const newIndex = currentIndex === 0 ? ads.length - 1 : currentIndex - 1;
      setCurrentIndex(newIndex);
      trackImpression(ads[newIndex].id);
    }
  };

  const goToAd = (index) => {
    setCurrentIndex(index);
    trackImpression(ads[index].id);
  };

  // Auto-rotate ads every 5 seconds
  useEffect(() => {
    if (ads.length > 1) {
      const timer = setInterval(nextAd, 5000);
      return () => clearInterval(timer);
    }
  }, [ads.length, nextAd]);

  if (loading) {
    return null;
  }

  if (ads.length === 0) {
    return null;
  }

  const currentAd = ads[currentIndex];

  return (
    <div className="featured-ads-carousel">
      <div className="carousel-container">
        <div 
          className="ad-item"
          onClick={() => handleAdClick(currentAd)}
          style={{ cursor: currentAd.url || currentAd.businessId ? 'pointer' : 'default' }}
        >
          <div className="ad-image-wrapper">
            {currentAd.imageUrl && (
              <img src={currentAd.imageUrl} alt={currentAd.title || 'Featured Ad'} />
            )}
            <div className="featured-badge">Featured</div>
          </div>
          {currentAd.title && (
            <div className="ad-content">
              <h3>{currentAd.title}</h3>
              {currentAd.description && <p>{currentAd.description}</p>}
            </div>
          )}
        </div>

        {ads.length > 1 && (
          <>
            <button className="carousel-btn prev" onClick={prevAd}>
              ‹
            </button>
            <button className="carousel-btn next" onClick={nextAd}>
              ›
            </button>
            <div className="carousel-dots">
              {ads.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToAd(index)}
                  aria-label={`Go to ad ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FeaturedAdsCarousel;
